import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton, 
  Typography, 
  Box, 
  Avatar, 
  Grid, 
  Paper, 
  Button,
  Card,
  CardContent,
  CardActions,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { COLORS } from '../config/colors';

// Base URL for profile images
const S3ProfileImageBaseUrl = 'https://fm-profile-imgs.s3.us-east-2.amazonaws.com';

// Function to get LinkedIn profile image URL
const getProfileImageUrl = (profile) => {
  if (!profile) return null;
  
  // Check if profile has ext_refs
  if (profile.ext_refs && Array.isArray(profile.ext_refs)) {
    // Find LinkedIn reference
    const linkedInRef = profile.ext_refs.find(ref => 
      ref.source === "LinkedIn" || ref.source === "LinkedIn"
    );
    
    if (linkedInRef && linkedInRef.source_ref) {
      // Return the S3 URL with the LinkedIn ID
      return `${S3ProfileImageBaseUrl}/${linkedInRef.source_ref}.jpg`;
    }
  }
  
  // Fallback to random images if no LinkedIn reference found
  const randomImages = [
    'https://randomuser.me/api/portraits/women/44.jpg',
    'https://randomuser.me/api/portraits/men/75.jpg',
    'https://randomuser.me/api/portraits/women/68.jpg',
  ];
  
  return randomImages[Math.floor(Math.random() * randomImages.length)];
};

// Function to get Findem profile URL
const getFindemProfileUrl = (profile) => {
  if (!profile) return null;
  
  // Check if profile has ext_refs
  if (profile.ext_refs && Array.isArray(profile.ext_refs)) {
    // Find Findem reference
    const findemRef = profile.ext_refs.find(ref => 
      ref.source === "Findem"
    );
    
    if (findemRef && findemRef.url) {
      return findemRef.url;
    }
  }
  
  // Fallback to profile ID if available
  if (profile.id) {
    return `https://app-next.findem.ai/profiles/${profile.id}`;
  }
  
  return null;
};

const CandidateCard = ({ candidate, index, profileId }) => {
  // Extract data from the candidate object
  const name = unescape(candidate?.candidate?.full_name) || 'Unknown Name';
  
  // Generate email from name and company if available
  const currentCompany = candidate?.workexp?.find(exp => exp.duration?.to_present)?.company?.name;
  const email = candidate?.candidate?.email || 
    (name && currentCompany ? 
      `${name.split(' ')[0].toLowerCase()[0]}${name.split(' ')[1]?.toLowerCase() || ''}@${currentCompany?.toLowerCase().replace(/\s+/g, '')}.com` : 
      'unknown@example.com');
  
  // Get current title
  const title = candidate?.workexp
    ?.find(exp => exp.duration?.to_present)
    ?.projects?.map(proj => proj.role_and_group?.title)
    .filter(Boolean)[0] || 'Software Engineer';
  
  // Get company name
  const company = candidate?.workexp
    ?.find(exp => exp.duration?.to_present)
    ?.company?.name || '';
  
  // Get location
  const city = candidate?.candidate?.location?.city || '';
  const state = candidate?.candidate?.location?.state || '';
  const country = candidate?.candidate?.location?.country || '';
  
  // Format location as "City, State" or "City, Country"
  let location = '';
  if (city) {
    if (state) {
      location = `${city}, ${state}`;
    } else if (country) {
      location = `${city}, ${country}`;
    } else {
      location = city;
    }
  } else {
    location = 'Unknown Location';
  }

  return (
    <Card 
      sx={{
        maxWidth: 280,
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <CardContent sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        textAlign: 'center',
        pt: 3,
        pb: 1,
        flexGrow: 1
      }}>
        <Avatar 
          src={getProfileImageUrl(candidate)} 
          alt={name}
          sx={{ 
            width: 80, 
            height: 80, 
            mb: 2,
            border: '3px solid white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        />
        
        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {name}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {email}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 'medium' }}>
          {title}
          {company && ` at ${company}`}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {location}
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 2,
          mb: 1
        }}>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <EmailIcon />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <MessageIcon />
          </IconButton>
          <IconButton 
            size="small" 
            sx={{ color: 'text.secondary' }}
            component="a"
            href={getFindemProfileUrl(candidate)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <PersonIcon />
          </IconButton>
        </Box>
      </CardContent>
      
      <CardActions sx={{ 
        borderTop: '1px solid rgba(0,0,0,0.08)',
        justifyContent: 'center',
        p: 1
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          width: '100%',
          px: 2
        }}>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <ThumbUpIcon />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <ThumbDownIcon />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <BookmarkIcon />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </CardActions>
    </Card>
  );
};

const CandidateModal = ({ open, onClose, candidates, onSaveSearch }) => {
  // Add logging to track when the modal is rendered and what data it receives
  console.log('CandidateModal - Rendering with props:', { 
    open, 
    candidatesExists: candidates ? true : false,
    candidatesLength: candidates ? Object.keys(candidates).length : 0 
  });

  // Convert candidates object to array for rendering
  const candidateArray = candidates && Object.keys(candidates).length > 0 
    ? Object.entries(candidates)
        .slice(0, 9)
        .map(([id, data]) => ({ id, ...data }))
    : [];
  
  console.log('CandidateModal - Processed candidateArray length:', candidateArray.length);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: 2
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Sample Matching Candidates</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {candidateArray.length > 0 ? (
            candidateArray.map((candidate, index) => (
              <Grid item xs={12} sm={6} md={4} key={candidate?.id || index}>
                <CandidateCard 
                  candidate={candidate} 
                  index={index} 
                  profileId={candidate.id}
                />
              </Grid>
            ))
          ) : (
            // Fallback to show 9 placeholder cards if no candidates
            Array.from({ length: 9 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  sx={{
                    maxWidth: 280,
                    borderRadius: '16px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    overflow: 'visible',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    textAlign: 'center',
                    pt: 3,
                    pb: 1,
                    flexGrow: 1
                  }}>
                    <Avatar 
                      src={`https://randomuser.me/api/portraits/${index === 0 ? 'women/44' : index === 1 ? 'men/75' : 'women/68'}.jpg`} 
                      alt="Placeholder"
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        mb: 2,
                        border: '3px solid white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                    
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {index === 0 ? 'Lilly Windrof' : index === 1 ? 'Darren Falesco' : 'Benediah Benediah'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {index === 0 ? 'lwindrof@dataform.com' : 
                       index === 1 ? 'dfalesco@scaleup.com' : 'bbenediah'}
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                      {index === 0 || index === 1 ? 'Senior Staff Software Engineer' : 'Senior Software Engineer'}
                      {index === 0 ? ' at DataForm' : 
                       index === 1 ? ' at ScaleUp' : ''}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {index === 0 ? 'New York, NY' : 
                       index === 1 ? 'Atlanta, GA' : 'Boston, MA'}
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      gap: 2,
                      mb: 1
                    }}>
                      <IconButton size="small" sx={{ color: 'text.secondary' }}>
                        <EmailIcon />
                      </IconButton>
                      <IconButton size="small" sx={{ color: 'text.secondary' }}>
                        <MessageIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        sx={{ color: 'text.secondary' }}
                        component="a"
                        href={`https://app-next.findem.ai/profiles/sample-${index}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <PersonIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                  
                  <CardActions sx={{ 
                    borderTop: '1px solid rgba(0,0,0,0.08)',
                    justifyContent: 'center',
                    p: 1
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      width: '100%',
                      px: 2
                    }}>
                      <IconButton size="small" sx={{ color: 'text.secondary' }}>
                        <ThumbUpIcon />
                      </IconButton>
                      <IconButton size="small" sx={{ color: 'text.secondary' }}>
                        <ThumbDownIcon />
                      </IconButton>
                      <IconButton size="small" sx={{ color: 'text.secondary' }}>
                        <BookmarkIcon />
                      </IconButton>
                      <IconButton size="small" sx={{ color: 'text.secondary' }}>
                        <RefreshIcon />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button 
            onClick={() => {
              onClose();
              if (onSaveSearch) {
                onSaveSearch();
              } else {
                // Fallback to opening a generic Findem search URL
                window.open('https://app-next.findem.ai/searches', '_blank');
              }
            }}
            sx={{ 
              textTransform: 'none',
              color: '#3100F7',
              '&:hover': {
                backgroundColor: 'rgba(49, 0, 247, 0.04)'
              }
            }}
            endIcon={<ArrowForwardIosIcon sx={{ fontSize: '0.8rem' }} />}
          >
            See all people
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateModal;
